import { useEffect, useRef } from "react";
import { toast } from "sonner";

/**
 * Polls /api/system/version and pops a "new version available" toast when
 * the server's app_version differs from what the client booted with.
 *
 * The check is deliberately quiet:
 *   - waits until the first successful fetch to capture a baseline
 *   - polls every 90s + on window focus
 *   - only shows the toast once per session (dismissThis prevents nagging)
 *   - the "Refresh" action reloads with cache-bust to grab the new bundle
 */
export function useVersionCheck({ intervalMs = 90000 } = {}) {
  const baselineRef = useRef(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/system/version`;

    let cancelled = false;

    const check = async () => {
      try {
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json();
        const remote = data?.app_version;
        if (!remote || cancelled) return;

        if (baselineRef.current == null) {
          baselineRef.current = remote;
          return;
        }
        if (remote !== baselineRef.current && !notifiedRef.current) {
          notifiedRef.current = true;
          toast.success(`New version v${remote} is live`, {
            description:
              "Reload to pick up the latest build. Your work will not be lost.",
            duration: Infinity,
            action: {
              label: "Refresh",
              onClick: () => {
                // Cache-bust query string forces the browser to skip the
                // stale SW/HTTP cache and re-download index.html.
                const bust = `?v=${encodeURIComponent(remote)}`;
                window.location.href = window.location.pathname + bust;
              },
            },
            id: "app-version-update",
          });
        }
      } catch (_) {
        // Silent — offline or backend blip. The next tick will retry.
      }
    };

    check();
    const timer = setInterval(check, intervalMs);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [intervalMs]);
}
